<template>
  <v-form ref="form" v-model="valid" lazy-validation class="floatleft">
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <div>
            <v-row class="details" v-if="isAdmin">
              <v-col cols="12"><h5 class="details-title">Informations générales</h5></v-col>
              <v-col cols="12" md="4" v-if="possible_assistances.length">
                <v-autocomplete
                  :label="descriptions.as_id"
                  :items="possible_assistances"
                  :item-text="item => `${item.name}${item.society ? ` (${item.society})` : ''}`"
                  item-value="id"
                  v-model="localStudent.as_id"
                  required
                  :error-messages="getErrors('assistance', $v.localStudent.as_id)"
                  @input="$v.localStudent.as_id.$touch()"
                ></v-autocomplete>
                <v-btn
                  v-if="localStudent.as_id && selectedAssistant"
                  small
                  color="primary"
                  text
                  @click="navigateToAssistant"
                  class="mx-0 mt-2"
                >
                  <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                  Info {{ selectedAssistantDisplayName }}
                </v-btn>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-if="countries.length"
                  v-model="localStudent.origin"
                  :items="countries"
                  item-text="title"
                  item-value="code"
                  :label="descriptions.origin"
                  prepend-icon="mdi-city"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  :label="descriptions.gender"
                  v-model="localStudent.gender"
                  :items="['Masculin', 'Féminin', 'Non-binaire']"
                  :rules="[() => !!localStudent.gender || 'Champ obligatoire']"
                  :error-messages="getErrors('Genre', $v.localStudent.gender)"
                  @focus="$v.localStudent.gender.$touch()"
                ></v-select>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  :label="descriptions.last_name"
                  v-model="localStudent.last_name"
                  required
                  :error-messages="getErrors('Nom', $v.localStudent.last_name)"
                  @change="$v.localStudent.last_name.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  :label="descriptions.first_name"
                  v-model="localStudent.first_name"
                  required
                  :error-messages="getErrors('Prénom', $v.localStudent.first_name)"
                  @change="$v.localStudent.first_name.$touch()"
                ></v-text-field>
              </v-col>
              <!-- Alphabétisé.e dans langue maternelle -->
              <v-col cols="12" md="4">
                <v-select
                  :items="[
                    { text: 'Oui', value: true },
                    { text: 'Non', value: false },
                  ]"
                  :label="descriptions.educated"
                  v-model="localStudent.educated"
                  clearable
                ></v-select>
              </v-col>
              <!-- Languages -->
              <v-col cols="12" md="4">
                <div v-if="localStudent.languages">
                  <strong>{{ localStudent.languages }}</strong>
                  Veuillez mettre à jour les valeurs ci-dessous.
                </div>
                <v-autocomplete
                  clearable
                  multiple
                  v-model="localStudent.langs"
                  :items="languages"
                  item-text="title"
                  item-value="code"
                  :label="descriptions.langs"
                  prepend-icon="mdi-translate"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <!-- Contacts -->
            <v-row class="details" v-if="isAdmin">
              <v-col cols="12">
                <h5 class="details-title">Adresse et contact</h5>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field :label="descriptions.mobile" v-model="localStudent.mobile"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  :label="descriptions.telephone_contact_person"
                  v-model="localStudent.telephone_contact_person"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  :label="descriptions.email"
                  v-model="localStudent.email"
                  :error-messages="getErrors('email', $v.localStudent.email)"
                  @input="$v.localStudent.email.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <address-field :address="localStudent.address" @input="updateAddress" />
                <v-text-field
                  :label="descriptions.address_other"
                  v-model="localStudent.address_other"
                  @input="updateAddressOther"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="details" v-if="isAdmin">
              <v-col cols="12">
                <h5 class="details-title">Profil civil</h5>
              </v-col>
              <v-col cols="12" md="3">
                <v-menu
                  ref="menuBirthDate"
                  v-model="menuBirthDate"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="localStudent.date_of_birth"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-on="on"
                      v-model="localStudent.date_of_birth"
                      :label="descriptions.date_of_birth"
                      prepend-icon="mdi-calendar"
                      readonly
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    locale="fr-CH"
                    first-day-of-week="1"
                    v-model="localStudent.date_of_birth"
                    @change="saveBirthDate"
                    :active-picker.sync="activePicker"
                    :max="new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toLocaleDateString('en-CA')"
                    min="1950-01-01"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  :label="descriptions.marital_status"
                  v-model="localStudent.marital_status"
                  :items="['Célibataire', 'Marié(e)', 'Séparé(e)', 'Divorcé (e)', 'Veuf(-ve)']"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="localStudent.children"
                  :label="descriptions.children"
                  multiple
                  :items="validYears"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  :items="[
                    'N',
                    'B-réfugié',
                    'B',
                    'F-réfugié',
                    'F',
                    'S-provisoire',
                    'S',
                    'C',
                    'Sans permis de séjour valable',
                  ]"
                  :label="descriptions.residence_permit"
                  v-model="localStudent.residence_permit"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  ref="menuResidencyDate"
                  :close-on-content-click="false"
                  v-model="menuResidencyDate"
                  :nudge-right="40"
                  :return-value.sync="localStudent.entry_date"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-on="on"
                      v-model="localStudent.resides_in_the_canton_since"
                      :label="descriptions.resides_in_the_canton_since"
                      prepend-icon="mdi-calendar"
                      readonly
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    locale="fr-CH"
                    first-day-of-week="1"
                    v-model="localStudent.resides_in_the_canton_since"
                    @input="$refs.menuResidencyDate.save(localStudent.resides_in_the_canton_since)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  :items="[
                    { text: 'Oui', value: true },
                    { text: 'Non', value: false },
                  ]"
                  :label="descriptions.has_driving_licence_valid_in_switzerland"
                  v-model="localStudent.has_driving_licence_valid_in_switzerland"
                  clearable
                ></v-select>
              </v-col>
            </v-row>
            <v-row class="details">
              <v-col cols="12">
                <h5 class="details-title">Parcours professionnel</h5>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  :label="descriptions.profession"
                  v-model="localStudent.profession"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  :items="[
                    { text: 'Qualifié professionnellement', value: true },
                    { text: 'Sans qualification professionnelle', value: false },
                  ]"
                  :label="descriptions.is_qualified"
                  v-model="localStudent.is_qualified"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="['Primaire', 'Secondaire', 'Tertiaire']"
                  :label="descriptions.sector"
                  v-model="localStudent.sector"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  :items="['0-3 ans', '3-10 ans', '+10 ans']"
                  :label="descriptions.experience_years"
                  v-model="localStudent.experience_years"
                  clearable
                ></v-select>
              </v-col>
            </v-row>
            <v-row class="details">
              <v-col cols="12">
                <h5 class="details-title">Objectifs d’intégration Agenda Intégration Suisse (AIS)</h5>
              </v-col>
              <v-col cols="12" md="12">
                <v-combobox
                  :items="[
                    'Intégration socioprofessionnelle',
                    'Formation Apprentissage',
                    'PAI',
                    'Haute Ecole',
                    'Université',
                    'Ecole polytechnique',
                  ]"
                  :label="descriptions.study_objective"
                  v-model="localStudent.study_objective"
                  clearable
                ></v-combobox>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  outlined
                  auto-grow
                  :label="descriptions.study_objective_comment"
                  v-model="localStudent.study_objective_comment"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  outlined
                  auto-grow
                  :label="descriptions.work_objective_comment"
                  v-model="localStudent.work_objective_comment"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  outlined
                  auto-grow
                  :label="descriptions.integration_objective_comment"
                  v-model="localStudent.integration_objective_comment"
                ></v-textarea>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions v-if="isCreateMode">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click.native="close">Annuler</v-btn>
        <v-btn :disabled="isDisabled" dark color="blue darken-1" @click.native="save">Sauvegarder</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>
<script>
import { mapGetters } from "vuex"
import Description from "@/models/Description"
import Student from "@/models/Student"
import Assistant from "@/models/Assistant"
import AddressField from "@/components/common/AddressField.vue"
import { cloneDeep } from "lodash"
import { required, email } from "vuelidate/lib/validators"
import _ from "lodash"

export default {
  components: {
    AddressField,
  },
  props: {
    isCreateMode: {
      type: Boolean,
      default: false,
    },
    formTitle: {
      type: String,
      default: "Modifier",
    },
    student: {
      type: Student,
      required: true,
    },
  },
  created: function () {
    axios.get("/api/v1/countries").then((data) => {
      this.countriesObject = data.data
    })
    axios.get("/api/v1/languages").then((data) => {
      this.languagesObject = data.data
    })
    Assistant.get()
  },
  data() {
    return {
      valid: true,
      menu2: false,
      menu3: false,

      menuEntryLevelDate: false,
      menuResidencyDate: false,
      menuBirthDate: false,
      test_statuts: [
        "A1 acquis",
        "A1 en cours d'acquisition",
        "A2 acquis",
        "A2 en cours d'acquisition",
        "B1 acquis",
        "B1 en cours d'acquisition",
        "B2 acquis",
        "B2 en cours d’acquisition",
        "non évaluable",
      ],
      localStudent: cloneDeep(this.student) || null,
      countriesObject: null,
      languagesObject: null,
      isDisabled: false,
      activePicker: null,
      debouncedSave: _.debounce(this.save, 500),
    }
  },
  methods: {
    testStatsPrefixed(prefix, counter) {
      let arr = []
      for (let index = 1; index <= counter; index++) {
        arr.push(prefix + index + " acquis")
        arr.push(prefix + index + " en cours d'acquisition")
      }
      arr.push("non évalué")
      return arr
    },
    isValid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }
      return true
    },
    saveDebounced() {
      if (this.isValid()) {
        this.debouncedSave()
      }
    },
    save() {
      if (this.isValid()) {
        this.$emit("save", this.localStudent)
      }
    },
    getErrors(name, model) {
      const errors = []
      if (!model.$dirty) {
        return errors
      }
      if (model.$invalid) {
        errors.push(`${name} erreur`)
      }
      return errors
    },
    close() {
      {
        this.$router.go(-1)
      }
    },
    saveBirthDate() {
      this.$refs.menuBirthDate.save(this.localStudent.date_of_birth)
    },
    updateAddress(address) {
      this.localStudent.address = address
    },
    updateAddressOther(other) {
      this.localStudent.address_other = other
    },
    navigateToAssistant() {
      if (this.selectedAssistant) {
        this.$router.push({
          path: '/assistants',
          query: { search: this.selectedAssistant.name }
        })
      }
    },
  },
  watch: {
    menuBirthDate(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"))
    },
    localStudent: {
      deep: true,
      handler() {
        if (this.isCreateMode) {
          return
        }
        this.saveDebounced(this.localStudent)
      },
    },
  },
  computed: {
    possible_assistances() {
      return Assistant.query().get()
    },
    selectedAssistant() {
      if (!this.localStudent.as_id) return null
      return Assistant.query().where('id', this.localStudent.as_id).first()
    },
    selectedAssistantDisplayName() {
      if (!this.selectedAssistant) return ''
      const { name, society } = this.selectedAssistant
      return `${name} (${society})`
    },

    validYears: () => {
      let max = new Date().getFullYear()
      let min = max - 18
      let years = []
      for (let i = max; i >= min; i--) {
        years.push(i)
      }
      return years
    },
    countries() {
      // convert key-value country objects {"AF": "Afghanistan"}
      // to {"code": "AF", "title": "Afghanistan"} array
      if (!this.countriesObject) {
        return []
      }
      let cObj = this.countriesObject
      return Object.keys(cObj).map(function (key) {
        let element = {}
        element["code"] = key
        element["title"] = cObj[key]
        return element
      })
    },
    languages() {
      // convert key-value language objects {"ab": "abkhaze"}
      // to {"code": "ab", "title": "abkhaze"} array
      if (!this.languagesObject) {
        return []
      }
      let languagesObj = this.languagesObject
      return Object.keys(languagesObj).map(function (key) {
        let element = {}
        element["code"] = key
        element["title"] = languagesObj[key]
        return element
      })
    },
    descriptions() {
      return Description.every();
    },  
    ...mapGetters("Auth", ["isAdmin", "isProfessor"]),
  },
  validations: {
    localStudent: {
      gender: {
        required,
      },
      first_name: {
        required,
      },
      last_name: {
        required,
      },
      email: {
        required,
        email,
      },
      as_id: {
        required,
      },
    },
  },
}
</script>
<style scoped>
.floatleft {
  float: left;
}
.details {
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.05),
    0 1px 2px 3px rgba(0, 0, 0, 0.06);
  padding: 10px;
  margin-bottom: 20px;
}
.details-title {
  margin-top: 0;
  margin-bottom: 0;
  color: rgb(153, 152, 152);
}
</style>
